import { createReducer, on } from '@ngrx/store';
import { getIsSiriusEnabled } from '@app/common/sirius-util';
import { PLAuthError, PLAuthPending, PLAuthSuccess } from './actions';
import { PLAuthState } from './state';

// Pure initial state for type safety
const initialState: PLAuthState = {
  authState: 'init',
  isSiriusEnabled: false,
};

// Factory for initial state, reading from localStorage or using getIsSiriusEnabled
export function getInitialAuthState(): PLAuthState {
  const isSiriusEnabled = getIsSiriusEnabled();
  return {
    ...initialState,
    isSiriusEnabled,
  };
}

function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );
  return JSON.parse(jsonPayload);
}

export const reducer = createReducer(
  initialState,
  on(
    PLAuthPending,
    (state): PLAuthState => ({
      ...state,
      authState: 'pending',
    })
  ),
  on(PLAuthSuccess, (state, { user, token }): PLAuthState => {
    const jwt = parseJwt(token);
    // Use state.isSiriusEnabled instead of module variable
    if (state.isSiriusEnabled) {
      const currentUser = user
        ? { ...state.currentUser, ...user } // Merge for shallow immutability
        : state.currentUser; // Keep the same reference if no user provided

      const newToken = token || state.token; // Only update if new token exists
      const newHijacked = jwt.plru === undefined ? false : jwt.plru !== jwt.sub;

      // Early return if nothing has changed to prevent state mutation
      if (
        currentUser === state.currentUser &&
        newToken === state.token &&
        newHijacked === state.hijacked
      ) {
        return state;
      }

      return {
        ...state,
        authState: 'success',
        // Only update user if provided, otherwise keep existing
        currentUser,
        // Only update token if provided, otherwise keep existing
        token: newToken,
        // Only update hijacked status if we have a new JWT (plru is only from auth);
        hijacked: newHijacked,
      };
    }
    return {
      ...state,
      authState: 'success',
      currentUser: user,
      token,
      hijacked: jwt.plru !== jwt.sub,
    };
  }),
  on(
    PLAuthError,
    (state): PLAuthState => ({
      ...state,
      authState: 'error',
      currentUser: null,
      token: null,
    })
  )
);
